import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

function VideoSection({ player1, player2, mashupVideo, videoLoading = false, videoStatus = null }) {
  // Debug logging
  React.useEffect(() => {
    console.log('[VideoSection] Props received:', {
      player1: player1?.name,
      player2: player2?.name,
      mashupVideo: mashupVideo ? {
        mashup_type: mashupVideo.mashup_type,
        video_path: mashupVideo.video_path,
        video_filename: mashupVideo.video_filename
      } : null
    });
  }, [player1, player2, mashupVideo]);

  // Extract YouTube video ID
  const getVideoId = (videoIdOrSearch) => {
    if (!videoIdOrSearch) return null;
    
    // If it's already a video ID (11 characters, alphanumeric)
    if (/^[a-zA-Z0-9_-]{11}$/.test(videoIdOrSearch)) {
      return videoIdOrSearch;
    }
    
    // If it's a YouTube URL, extract ID
    const urlMatch = videoIdOrSearch.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (urlMatch) {
      return urlMatch[1];
    }
    
    return null;
  };

  const player1VideoId = getVideoId(player1?.youtube_video_id);
  const player2VideoId = getVideoId(player2?.youtube_video_id);

  // Check if we have a processed video or embedded videos
  const isProcessedVideo = mashupVideo?.mashup_type === 'processed' && mashupVideo?.video_path;
  const isEmbedMode = mashupVideo?.mashup_type === 'embed' && mashupVideo?.player1_video && mashupVideo?.player2_video;
  
  // Video is processing if:
  // 1. videoLoading is true (polling is active)
  // 2. videoStatus is 'processing' 
  // 3. videoStatus is undefined/null and no mashupVideo exists (initial state)
  // BUT NOT if videoStatus is 'ready' (even if videoLoading is still true, video is ready)
  const isVideoProcessing = (videoLoading || videoStatus === 'processing' || (!videoStatus && !mashupVideo)) 
                           && videoStatus !== 'ready';
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const processedVideoUrl = isProcessedVideo 
    ? `${API_URL}${mashupVideo.video_path}` 
    : null;
  
  // Debug logging
  React.useEffect(() => {
    console.log('[VideoSection] State:', {
      hasMashupVideo: !!mashupVideo,
      mashupVideoType: mashupVideo?.mashup_type,
      videoPath: mashupVideo?.video_path,
      videoLoading,
      videoStatus,
      isProcessedVideo,
      isVideoProcessing,
      willShowVideo: isProcessedVideo && !isVideoProcessing
    });
  }, [mashupVideo, videoLoading, videoStatus, isProcessedVideo, isVideoProcessing]);

  // No YouTube playlist fallback - only processed videos are supported

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl">Player Highlights Comparison</CardTitle>
        <CardDescription>
          Watch highlights and mashup videos of both players
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {/* Priority 1: Show processed video if available */}
          {processedVideoUrl ? (
            <div className="space-y-4">
              <div>
                <h4 className="text-lg font-semibold mb-2">
                  {player1?.name || 'Player 1'} vs {player2?.name || 'Player 2'} - Highlights Mashup
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  ✨ AI-processed video combining highlights from both players
                </p>
              </div>
              <div className="rounded-lg overflow-hidden border">
                <video
                  controls
                  width="100%"
                  height="500"
                  className="w-full"
                  crossOrigin="anonymous"
                  preload="metadata"
                >
                  <source src={processedVideoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="text-sm text-muted-foreground">
                🎬 This video was automatically created by combining highlights from both players.
                {mashupVideo?.file_size && ` Size: ${(mashupVideo.file_size / 1024 / 1024).toFixed(2)} MB`}
              </p>
            </div>
          ) : isEmbedMode ? (
            <div className="space-y-4">
              <div>
                <h4 className="text-lg font-semibold mb-2">
                  {mashupVideo.player1_name || player1?.name || 'Player 1'} vs {mashupVideo.player2_name || player2?.name || 'Player 2'} - Side-by-Side Comparison
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  📺 Watch both players' highlights simultaneously
                  {mashupVideo.fallback_reason && (
                    <span className="block mt-1 text-xs text-orange-600">
                      ℹ️ {mashupVideo.fallback_reason}
                    </span>
                  )}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h5 className="font-medium text-sm">{mashupVideo.player1_name || player1?.name || 'Player 1'}</h5>
                  <div className="rounded-lg overflow-hidden border aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src={mashupVideo.player1_video.embedUrl}
                      title={mashupVideo.player1_video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {mashupVideo.player1_video.title}
                  </p>
                </div>
                <div className="space-y-2">
                  <h5 className="font-medium text-sm">{mashupVideo.player2_name || player2?.name || 'Player 2'}</h5>
                  <div className="rounded-lg overflow-hidden border aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src={mashupVideo.player2_video.embedUrl}
                      title={mashupVideo.player2_video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {mashupVideo.player2_video.title}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                💡 Tip: You can watch both videos simultaneously for a direct comparison
              </p>
            </div>
          ) : isVideoProcessing ? (
            <div className="text-center p-12 rounded-lg border bg-muted/50">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="relative">
                  <Loader2 className="h-16 w-16 animate-spin text-primary" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl">🎥</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-lg font-medium">Generating mashup video...</p>
                  <p className="text-sm text-muted-foreground">
                    Downloading and processing highlights from both players in the background
                  </p>
                  <div className="mt-4 space-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse"></div>
                      <span>Videos are being downloaded in parallel</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <span>This may take 1-2 minutes</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <div className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      <span className="text-primary font-medium">Video will appear automatically when ready</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                {player1VideoId && (
                  <Button variant="outline" asChild>
                    <a 
                      href={`https://www.youtube.com/watch?v=${player1VideoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {player1?.name} Highlights
                    </a>
                  </Button>
                )}
                {player2VideoId && (
                  <Button variant="outline" asChild>
                    <a 
                      href={`https://www.youtube.com/watch?v=${player2VideoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {player2?.name} Highlights
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ) : videoStatus === 'failed' ? (
            <div className="text-center p-12 rounded-lg border bg-destructive/10">
              <p className="text-lg font-medium mb-2">⚠️ Video generation failed</p>
              <p className="text-sm text-muted-foreground mb-6">
                We couldn't generate the mashup video, but you can still view individual player highlights below.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {player1VideoId && (
                  <Button variant="outline" asChild>
                    <a 
                      href={`https://www.youtube.com/watch?v=${player1VideoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {player1?.name} Highlights
                    </a>
                  </Button>
                )}
                {player2VideoId && (
                  <Button variant="outline" asChild>
                    <a 
                      href={`https://www.youtube.com/watch?v=${player2VideoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {player2?.name} Highlights
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center p-12 rounded-lg border bg-muted/50">
              <p className="text-lg font-medium mb-2">🎥 No video available</p>
              <p className="text-sm text-muted-foreground mb-6">
                Video mashup is not available for this comparison.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default VideoSection;
